
import React, { useState } from "react"
import { Plus, Edit, Trash2, Settings } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useCommonAreaRules, useCreateRule, useUpdateRule, useDeleteRule } from "@/hooks/useCommonAreaRules"
import { useAuth } from "@/hooks/useAuth"

interface GerenciarRegrasModalProps {
  children: React.ReactNode
}

const GerenciarRegrasModal = ({ children }: GerenciarRegrasModalProps) => {
  const [open, setOpen] = useState(false)
  const [newRule, setNewRule] = useState("")
  const [editingRule, setEditingRule] = useState<{ id: string; text: string } | null>(null)
  
  const { profile } = useAuth()
  const { data: rules, isLoading } = useCommonAreaRules()
  const createRuleMutation = useCreateRule()
  const updateRuleMutation = useUpdateRule()
  const deleteRuleMutation = useDeleteRule()

  const canManageRules = profile?.role === 'sindico' || profile?.role === 'administracao'

  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newRule.trim()) return

    try {
      await createRuleMutation.mutateAsync({ rule_text: newRule.trim() })
      setNewRule("")
    } catch (error) {
      console.error("Erro ao criar regra:", error)
    }
  }

  const handleUpdateRule = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingRule || !editingRule.text.trim()) return

    try {
      await updateRuleMutation.mutateAsync({
        id: editingRule.id,
        rule_text: editingRule.text.trim()
      })
      setEditingRule(null)
    } catch (error) {
      console.error("Erro ao atualizar regra:", error)
    }
  }

  const handleDeleteRule = async (id: string) => {
    try {
      await deleteRuleMutation.mutateAsync(id)
    } catch (error) {
      console.error("Erro ao deletar regra:", error)
    }
  }

  if (!canManageRules) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Gerenciar Regras das Áreas Comuns</span>
          </DialogTitle>
          <DialogDescription>
            Adicione, edite ou remova as regras importantes para as áreas comuns do condomínio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Adicionar nova regra */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Adicionar Nova Regra</Label>
            <form onSubmit={handleCreateRule} className="space-y-3">
              <Textarea
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
                placeholder="Digite a nova regra..."
                className="min-h-[80px]"
              />
              <Button 
                type="submit" 
                disabled={!newRule.trim() || createRuleMutation.isPending}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {createRuleMutation.isPending ? "Adicionando..." : "Adicionar Regra"}
              </Button>
            </form>
          </div>

          {/* Lista de regras existentes */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Regras Existentes</Label>
            
            {isLoading ? (
              <p className="text-center text-gray-500">Carregando regras...</p>
            ) : rules && rules.length > 0 ? (
              <div className="space-y-3">
                {rules.map((rule) => (
                  <div key={rule.id} className="p-3 border rounded-lg space-y-2">
                    {editingRule?.id === rule.id ? (
                      <form onSubmit={handleUpdateRule} className="space-y-2">
                        <Textarea
                          value={editingRule.text}
                          onChange={(e) => setEditingRule({ ...editingRule, text: e.target.value })}
                          className="min-h-[60px]"
                        />
                        <div className="flex space-x-2">
                          <Button 
                            type="submit" 
                            size="sm"
                            disabled={updateRuleMutation.isPending}
                          >
                            {updateRuleMutation.isPending ? "Salvando..." : "Salvar"}
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingRule(null)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <p className="text-sm">{rule.rule_text}</p>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingRule({ id: rule.id, text: rule.rule_text })}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Editar
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600">
                                <Trash2 className="h-3 w-3 mr-1" />
                                Remover
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Remoção</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja remover esta regra? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRule(rule.id)}
                                  disabled={deleteRuleMutation.isPending}
                                >
                                  {deleteRuleMutation.isPending ? "Removendo..." : "Remover"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                Nenhuma regra cadastrada ainda.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default GerenciarRegrasModal
